// AppRoutes.tsx
import { useRoutes } from "react-router";
import { useRoleStore } from "@/store/useRoleStore";
import { userRoutesAcceptor } from "./useAcceptor";
import { useUserRoutes } from "./useRouter";
import { useEffect, useRef } from 'react';

/**
 * AppRoutes
 *
 * مدیریت مسیرهای اپلیکیشن بر اساس نقش کاربر:
 * - بررسی نقش کاربر از store و localStorage
 * - انتخاب مجموعه مسیر مناسب بر اساس نقش
 * - استفاده از hook `useRoutes` برای رندر کردن مسیرهای تعیین شده
 *
 * این کامپوننت به طور داینامیک مسیرهای اپلیکیشن را برای کاربران مختلف (مانند پذیرنده یا سایر نقش‌ها) مدیریت می‌کند.
 *
 * @returns JSX.Element | null — مسیرهای مناسب بر اساس نقش کاربر
 */
export default function AppRoutes() {
  const { role } = useRoleStore();
  const roleUser = localStorage.getItem("roleEnter");
  const routes = roleUser === "merchant" ? userRoutesAcceptor : useUserRoutes;
  
  // Inactivity timer logic - properly typed for TypeScript
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    const clearStorageAndRedirect = () => {
      // Clear all localStorage except possibly 'theme' if you want to keep it
      const preserveKeys = ['theme']; // Preserve theme setting
      
      Object.keys(localStorage).forEach(key => {
        if (!preserveKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Redirect to login
      window.location.href = '/login';
    };
    
    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(clearStorageAndRedirect, 30 * 60 * 1000);
    };
    
    // Events to reset timer
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });
    
    // Start initial timer
    resetTimer();
    
    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, []);
  
  return useRoutes(routes);
}